// Import React dependencies.
import React, { useState } from 'react';
import { InterpreterFrom } from 'xstate';
import { v4 as uuidv4 } from 'uuid';
import { ContainerElement, ITransclusionReference } from '@typedream/core';
import styled from 'styled-components';
import { useActor } from '@xstate/react';
import { flip, offset, shift } from '@floating-ui/react-dom';
import { toDOMNode } from '@udecode/plate-core';
import styles from '../SideToolbar.module.css';
import { SyncedMachineType } from '../../../nodes/container/fsm/Synced.fsm';
import { getTPlugin, useTPlateEditorRef } from '../../../plate';
import { ELEMENT_CONTAINER } from '../../../pluginKeys';
import { ContainerElementPluginOptions } from '../../../nodes/container/types';
import { OverlayPortal } from '../../../overlay/OverlayPortal';
import { Floating } from '../../../components/Floating';
import { useSelectedElementEntry } from '../../../plugins/elementSelection';

// const StyledSelectSyncBox = styled.div`
//   position: absolute;
//   top: calc(100% - 40px);
//   width: 100%;
//   display: flex;
//   align-items: flex-start;
//   justify-content: center;
//   cursor: pointer;
// `;

const StyledSyncSuggestionTitle = styled.div`
  padding: 10px 15px;
  text-align: left;
  color: var(--text-lighter);
`;

const StyledSyncSuggestion = styled.div`
  text-align: left;
  padding: 10px 15px;
  border-radius: 5px;
  line-height: 1.2;

  &:hover {
    background-color: var(--background-highlight);
    cursor: pointer;
  }
`;

function SyncContainerSuggestions(props: {
  machine: InterpreterFrom<SyncedMachineType>,
}) {
  const editor = useTPlateEditorRef();
  const { machine } = props;

  const selectedElementEntry = useSelectedElementEntry<ContainerElement>();

  const [machineState, machineSend] = useActor(machine);

  const [syncedReferences, setSyncedReferences] = useState<ITransclusionReference>({});

  const { options } = getTPlugin<ContainerElementPluginOptions>(
    editor,
    ELEMENT_CONTAINER,
  );

  if (!options) return null;

  const { getTransclusionReferences } = options;

  React.useEffect(() => {
    getTransclusionReferences().then((refs) => {
      setSyncedReferences(refs);
    });
  }, []);

  React.useEffect(() => {
    function handler() {
      machineSend({ type: 'ON_SHOW_TRANSCLUSION_SELECTION' });
    }

    function createHandler() {
      machineSend({ type: 'ON_CREATE_SYNCED_CONTAINER', newTransclusionID: uuidv4() });
    }

    editor.emitter.on('toolbar:toggle-synced-container-selector', handler);
    editor.emitter.on('toolbar:create-synced-container', createHandler);

    return () => {
      editor.emitter.off('toolbar:toggle-synced-container-selector', handler);
      editor.emitter.off('toolbar:create-synced-container', createHandler);
    };
  }, [editor]);

  if (!selectedElementEntry) return null;

  const [element] = selectedElementEntry;

  const domNode = toDOMNode(editor, element);

  if (
    !machineState.hasTag('ui:showing_suggestion_box')
    || !domNode
  ) {
    return null;
  }

  return (
    <OverlayPortal>
      <Floating
        reference={domNode}
        options={{
          placement: 'bottom',
          middleware: [
            offset(-6),
            flip(),
            shift({
              crossAxis: true,
              padding: { top: 8, bottom: 8 },
            }),
          ],
        }}
        autoUpdate
        className={styles.box}
      >
        <div style={{
          pointerEvents: 'auto',
        }}
        >
          <StyledSyncSuggestionTitle className="with-lighter-text-color my-1 is-subtitle-size">
            Use existing Synced Containers
          </StyledSyncSuggestionTitle>
          <div style={{
            height: 250,
            overflowY: 'scroll',
          }}
          >
            {syncedReferences ? Object.entries(syncedReferences)?.map(([id, trans]) => (
              <StyledSyncSuggestion
                role="none"
                onClick={() => {
                  machineSend({ type: 'ON_SELECT_EXISTING_TRANSCLUSION', transclusionID: id });
                }}
              >
                <div className="with-text-color is-size-7">
                  {trans.name}
                </div>
              </StyledSyncSuggestion>
            )) : <div className="with-lighter-text-color is-size-7">No existing Synced Container</div>}
          </div>
        </div>
      </Floating>
    </OverlayPortal>
  );
}

export default SyncContainerSuggestions;
